import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Button, List, Spin, Tabs, Typography } from 'antd'
import {
  selectCurrentActiveNotification,
  selectCurrentCommentData,
  selectCurrentFileData,
  selectNotificationsLoading,
  selectNotificationsWithAvatars
} from 'features/common/model/selectors/notificationsSelectors'
import {
  batchMarkNotificationsAsRead,
  fetchNotifications,
  fetchPreviewData,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  setCurrentActiveNotification,
  setIsNotificationsPopoverOpened,
} from 'features/common/model/slice/notificationsSlice'
import { NotificationItem } from 'features/common/ui/notifications/NotificationItem'
import { openFilesPreviewThunk, TabsNames } from 'features/files-preview/model/slice/filesPreviewSlice'
import { userIdSelector } from 'features/user/model/selectors/userSelectors'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import styled from 'styled-components'

import { NotificationPreview } from './NotificationPreview'

const { Text } = Typography

interface NotificationsPanelProps {
  onClose: () => void;
}

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ onClose: onCloseIgnored }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const userId = useAppSelector(userIdSelector)
  const notifications = useAppSelector(selectNotificationsWithAvatars)
  const currentNotification = useAppSelector(selectCurrentActiveNotification)
  const [activeTab, setActiveTab] = useState('all')
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewPosition, setPreviewPosition] = useState<{
    top?: number;
    bottom?: number;
    right: number;
    bottomAlign: boolean;
    initialRender: boolean;
  }>({
    top: 0,
    right: 0,
    bottomAlign: false,
    initialRender: true
  })
  const abortControllerRef = useRef<AbortController | null>(null)
  const notificationsPanelRef = useRef<HTMLDivElement | null>(null)

  const mentionNotifications = notifications.filter((notification) => notification.type === 'mention_in_comment')
  const replyNotifications = notifications.filter((notification) => notification.type === 'reply_comment')
  const newCommentsNotifications = notifications.filter((notification) => notification.type === 'new_comment')

  const currentFileData = useAppSelector(selectCurrentFileData)
  const currentCommentData = useAppSelector(selectCurrentCommentData)

  const loading = useAppSelector(selectNotificationsLoading)

  useEffect(() => {
    dispatch(fetchNotifications())
  }, [dispatch])

  const handleNotificationClick = (notification: any) => {
    if (currentFileData?.id !== notification.resources.find((resource) => resource.type === 'fs_version')?.id.split(':')[0]) {
      const fileId = notification.resources.find((resource) => resource.type === 'fs_version')?.id.split(':')[0]
      const commentId = notification.resources.find((resource) => resource.type === 'comment_message')?.id

      const callback = (assetInfo: any, commentInfo: any) => {
        dispatch(openFilesPreviewThunk({
          items: [assetInfo],
          currentItemId: assetInfo.id,
          totalItemsCount: 1,
          commentIdForScrollTo: commentInfo.id,
          activeTab: TabsNames.COMMENTS,
          navigate
        }))
      }

      dispatch(fetchPreviewData({
        fileId,
        commentId,
        signal: null,
        callback
      }))
    } else {
      dispatch(openFilesPreviewThunk({
        items: [currentFileData],
        currentItemId: currentFileData.id,
        totalItemsCount: 1,
        commentIdForScrollTo: currentCommentData?.id,
        activeTab: TabsNames.COMMENTS,
        navigate
      }))
    }

    dispatch(setIsNotificationsPopoverOpened(false))

    const currentFSVersionId = notification.resources.find((resource) => resource.type === 'fs_version')?.id
    const allNotificationIds = notifications.filter((item) => item.resources.find((resource) => resource.type === 'fs_version')?.id === currentFSVersionId)
    const allNotificationIdsToPatch = allNotificationIds.filter((item) => !item.is_viewed)

    if (allNotificationIdsToPatch.length === 1) {
      dispatch(markNotificationAsRead({ userId, notificationId: allNotificationIdsToPatch[0].id }))
    }

    if (allNotificationIdsToPatch.length > 1) {
      dispatch(batchMarkNotificationsAsRead({ userId, notificationIds: allNotificationIdsToPatch.map((item) => item.id) }))
    }
  }

  const handleNotificationHover = (notification: any, e: React.MouseEvent) => {
    const fileId = notification.resources.find((resource) => resource.type === 'fs_version')?.id.split(':')[0]
    const commentId = notification.resources.find((resource) => resource.type === 'comment_message')?.id

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const newAbortController = new AbortController()
    abortControllerRef.current = newAbortController

    const targetElement = e.currentTarget as HTMLElement
    const rect = targetElement.getBoundingClientRect()

    const viewportHeight = window.innerHeight

    const estimatedPreviewHeight = 500

    const hasSpaceToBottom = rect.top + estimatedPreviewHeight <= viewportHeight

    if (hasSpaceToBottom) {
      setPreviewPosition({
        top: rect.top,
        right: window.innerWidth - rect.left,
        bottomAlign: false,
        initialRender: true
      })
    } else {
      setPreviewPosition({
        bottom: viewportHeight - rect.bottom,
        right: window.innerWidth - rect.left,
        bottomAlign: true,
        initialRender: true
      })
    }

    setPreviewVisible(true)
    if (currentNotification?.id === notification.id) {
      return
    }

    dispatch(setCurrentActiveNotification(notification))

    dispatch(fetchPreviewData({
      fileId,
      commentId,
      signal: newAbortController.signal
    }))
  }

  const handleNotificationLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement

    if (!relatedTarget?.closest || relatedTarget?.closest('.preview-container')) {
      return
    }

    setPreviewVisible(false)
    dispatch(setCurrentActiveNotification(null))
  }

  const handlePreviewMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement

    if (!relatedTarget?.closest || relatedTarget?.closest('.notification-item')) {
      return
    }

    setPreviewVisible(false)
    dispatch(setCurrentActiveNotification(null))
  }

  const handlePreviewMeasured = (height: number) => {
    const currentPosition = { ...previewPosition }

    if (currentPosition.initialRender) {
      const viewportHeight = window.innerHeight

      if (!currentPosition.bottomAlign) {
        const top = currentPosition.top || 0

        if (top + height > viewportHeight) {
          setPreviewPosition({
            bottom: currentPosition.bottom || (viewportHeight - (top + height)),
            right: currentPosition.right,
            bottomAlign: true,
            initialRender: false
          })
        } else {
          setPreviewPosition({
            ...currentPosition,
            initialRender: false
          })
        }
      } else {
        setPreviewPosition({
          ...currentPosition,
          initialRender: false
        })
      }
    }
  }

  const renderTabContent = useCallback( (items: any[]) => {
    if (loading) {
      return (
        <SLoadingContainer>
          <Spin size="large" />
        </SLoadingContainer>
      )
    }

    if (items.length === 0) {
      return (
        <SEmptyContainer>
          <SEmptyImg src="/assets/placeholders-images/no-content.svg" />

          <SEmptyText>
            There is no notifications
          </SEmptyText>

          <SEmptyDescription>
            The available notifications will appear here
          </SEmptyDescription>
        </SEmptyContainer>
      )
    }

    const weekMs = 7 * 24 * 60 * 60 * 1000
    const now = Date.now()
    const weekAgo = now - weekMs
    const sortByCreatedDesc = (a: any, b: any) => new Date(b.created).getTime() - new Date(a.created).getTime()

    const last7DaysUnreadNotifications: any[] = []
    const last7DaysReadNotifications: any[] = []
    const othersUnreadNotifications: any[] = []
    const othersReadNotifications: any[] = []

    items.forEach(item => {
      const createdTime = new Date(item.created).getTime()
      const isLast7Days = createdTime > weekAgo
      if (isLast7Days) {
        if (!item.is_viewed) {
          last7DaysUnreadNotifications.push(item)
        } else {
          last7DaysReadNotifications.push(item)
        }
      } else {
        if (!item.is_viewed) {
          othersUnreadNotifications.push(item)
        } else {
          othersReadNotifications.push(item)
        }
      }
    })

    last7DaysUnreadNotifications.sort(sortByCreatedDesc)
    last7DaysReadNotifications.sort(sortByCreatedDesc)
    othersUnreadNotifications.sort(sortByCreatedDesc)
    othersReadNotifications.sort(sortByCreatedDesc)

    return (
      <>
        {(last7DaysUnreadNotifications.length || last7DaysReadNotifications.length > 0) && (
          <>
            <SPeriodTitle>
              Last 7 days
            </SPeriodTitle>

            <List
              dataSource={[...last7DaysUnreadNotifications, ...last7DaysReadNotifications]}
              renderItem={(item) => (
                <NotificationItem
                  notification={item}
                  onClick={handleNotificationClick}
                  onMouseEnter={(e) => handleNotificationHover(item, e)}
                  onMouseLeave={handleNotificationLeave as (e: React.MouseEvent) => void}
                  className="notification-item"
                />
              )}
            />
          </>
        )}

        {(othersUnreadNotifications.length || othersReadNotifications.length > 0) && (
          <>
            <SPeriodTitle>
              Others
            </SPeriodTitle>

            <List
              dataSource={[...othersUnreadNotifications, ...othersReadNotifications]}
              renderItem={(item) => (
                <NotificationItem
                  notification={item}
                  onClick={handleNotificationClick}
                  onMouseEnter={(e) => handleNotificationHover(item, e)}
                  onMouseLeave={handleNotificationLeave as (e: React.MouseEvent) => void}
                  className="notification-item"
                />
              )}
            />
          </>
        )}
      </>
    )
  }, [notifications, loading])

  const tabItems = [
    {
      key: 'all',
      label: `All (${notifications.length})`,
      children: (
        <STabContent className="tab-content">
          {renderTabContent(notifications)}
        </STabContent>
      ),
    },
    {
      key: 'comments',
      label: `Comments (${newCommentsNotifications.length})`,
      children: (
        <STabContent>
          {renderTabContent(newCommentsNotifications)}
        </STabContent>
      ),
    },
    {
      key: 'mentions',
      label: `Mentions (${mentionNotifications.length})`,
      children: (
        <STabContent className="tab-content">
          {renderTabContent(mentionNotifications)}
        </STabContent>
      ),
    },
    {
      key: 'reply',
      label: `Reply (${replyNotifications.length})`,
      children: (
        <STabContent className="tab-content">
          {renderTabContent(replyNotifications)}
        </STabContent>
      ),
    },
  ]

  return (
    <SNotificationsPanel ref={notificationsPanelRef}>
      <SHeader>
        <STitle>
          Notifications
        </STitle>
      </SHeader>

      <STabs
        activeKey={activeTab}
        onChange={setActiveTab}
        tabBarExtraContent={{
          right: (
            <Button
              type="text"
              onClick={() => dispatch(markAllNotificationsAsRead({ userId, notificationIds: notifications.filter((item) => !item.is_viewed).map((item) => item.id) }))}
              disabled={notifications.filter((item) => !item.is_viewed).length === 0}
            >
              Mark all as read
            </Button>
          )
        }}
        items={tabItems}
      />

      {previewVisible && (
        <NotificationPreview
          previewPosition={previewPosition}
          onPreviewMouseLeave={handlePreviewMouseLeave}
          onMeasure={handlePreviewMeasured}
        />
      )}
    </SNotificationsPanel>
  )
}

const SNotificationsPanel = styled.div`
  width: 100%;
  position: relative;
  padding-bottom: 24px;
`

const SHeader = styled.div`
  padding: 24px 24px 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #000000E0;
`

const STitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
`

const STabs = styled(Tabs)`
  .ant-tabs-nav {
    margin: 12px 24px 0;
  }

  .ant-tabs-tab-btn {
    outline: none!important;
  }
`

const STabContent = styled.div`
  max-height: max(calc(100vh - 200px), 700px);
  overflow-y: auto;

  @supports (-moz-appearance: none) { 
    scrollbar-color: var(--scroll-color) transparent; 
  }
  
  &::-webkit-scrollbar {
    width: 4px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-thumb {
    background-color: gray;
    border-radius: 14px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const SLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`

const SEmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px 0;
`

const SEmptyImg = styled.img`
  width: 216px;
  height: 138px;
`

const SEmptyText = styled(Text)`
  margin-top: 32px;
  font-size: 20px;
  font-weight: 600;
`

const SEmptyDescription = styled(Text)`
  margin-top: 20px;
  font-size: 14px;
  color: #000000A6;
`

const SPeriodTitle = styled.p`
  margin-top: 24px;
  padding-left: 24px;
  margin-bottom: 8px;
  color: #00000073;
  font-size: 14px;
  font-weight: 400;
`
